import i18n from 'lib/i18n';

/**
 * Returns the declarative menu definition for the customer_admin_left (cportal sidebar) slug.
 *
 * All permission evaluation happens at render time via filterMenu + PermissionManager
 * (see menuHelper.js). URL templates resolved by filterMenu:
 *   {{slug}}         — org slug
 *   {{engage_base}}  — withAccountHost(slug, engageDomain, locale)
 *   {{next_base}}    — withAccountHost(slug, nextDomain, '') with no trailing slash
 *
 * @param {object} environment - domain URLs
 * @param {string} environment.adminifiDomain    — adminifi domain (used only for data exports)
 * @param {string} environment.engageDomain      — base engage domain (subdomain injected by filterMenu)
 * @param {string} environment.nextDomain        — base next domain (subdomain injected by filterMenu)
 * @param {string} environment.dashifiDomain     — main cportal domain (MENU_DOMAIN_FOR_FRONT_END)
 */
export const customerAdminLeft = ({
  adminifiDomain,
  engageDomain,
  nextDomain,
  dashifiDomain,
}) => {

  const settingsCapabilities = [
    'WebhookAccess',
    'EmbedContent',
    'PublicApiAccess',
    'SamlSso',
    'TagManager',
  ];

  return {
    slug: 'customer_admin_left',
    submenu_items: [
      {
        id: '',
        slug: '',
        title: i18n.t('Home'),
        icon: 'house-chimney',
        font_family: '',
        css_class: '',
        permissions: [
          { has_rule_set_role: ['he_cc_fs_primary_managers'] },
          { has_rule_set: ['advisors'] },
        ],
        target_url: `${dashifiDomain}/{{slug}}/cportal/actionable_dashboard`,
        target_method: '',
        new_window: false,
        subsection: false,
        submenu_items: [],
        weight: 100,
      },
      {
        id: '',
        slug: '',
        title: i18n.t('Home'),
        icon: 'house-chimney',
        font_family: '',
        css_class: '',
        permissions: [{ has_rule_set: ['next_managers'], not_rule_set: ['advisors'] }],
        target_url: `${dashifiDomain}/{{slug}}/cportal/financial_launchpad`,
        target_method: '',
        new_window: false,
        subsection: false,
        submenu_items: [],
        weight: 100,
      },
      {
        id: '',
        slug: '',
        title: i18n.t('Workshop Topics'),
        icon: 'library',
        font_family: '',
        css_class: '',
        permissions: [{ has_rule_set: ['workshop_managers'] }],
        target_url: `${dashifiDomain}/{{slug}}/cportal/topics`,
        target_method: '',
        new_window: false,
        subsection: false,
        submenu_items: [],
        weight: 100,
      },
      {
        id: 'customizations',
        slug: 'customizations',
        title: i18n.t('Content'),
        icon: 'films',
        font_family: '',
        css_class: '',
        permissions: [
          { has_rule_set_role: ['he_cc_fs_primary_managers'] },
          { has_rule_set: [{ he_cc_fs_scorm_manager_names: 'scorm_manager' }] },
        ],
        target_url: '',
        target_method: '',
        new_window: false,
        subsection: false,
        weight: 100,
        submenu_items: [
          {
            id: '',
            slug: '',
            title: i18n.t('Content Library'),
            icon: 'ban',
            font_family: '',
            css_class: '',
            target_url: `${dashifiDomain}/{{slug}}/cportal/content_library`,
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
          {
            id: '',
            slug: '',
            title: i18n.t('Resources'),
            icon: 'ban',
            font_family: '',
            css_class: '',
            target_url: `${dashifiDomain}/{{slug}}/cportal/resources`,
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
          {
            id: '',
            slug: '',
            title: i18n.t('Policies'),
            icon: 'ban',
            font_family: '',
            css_class: '',
            target_url: `${dashifiDomain}/{{slug}}/cportal/policies`,
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
          {
            id: '',
            slug: '',
            title: i18n.t('Scorm Content Packages'),
            icon: 'ban',
            font_family: '',
            css_class: '',
            permissions: [{ has_rule_set: [{ he_cc_fs_scorm_manager_names: 'scorm_manager' }] }],
            target_url: `${dashifiDomain}/{{slug}}/cportal/scorm_packages`,
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
        ],
      },
      {
        id: '',
        slug: '',
        title: i18n.t('Users'),
        icon: 'circle-user',
        font_family: '',
        css_class: '',
        permissions: [
          { has_capabilities: ['TeamManagement'], has_rule_set: ['next_managers'] },
          { has_rule_set: ['he_cc_fs_scorm_manager_names'] },
        ],
        target_url: '',
        target_method: '',
        new_window: false,
        subsection: false,
        weight: 100,
        submenu_items: [
          {
            id: '',
            slug: '',
            title: i18n.t('Manage Users'),
            icon: '',
            font_family: '',
            css_class: '',
            permissions: [
              { has_capabilities: ['TeamManagement'], has_rule_set: ['next_managers'] },
              { has_rule_set: ['he_cc_fs_scorm_manager_names'] },
            ],
            target_url: `${dashifiDomain}/{{slug}}/cportal/users`,
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
          {
            id: '',
            slug: '',
            title: i18n.t('Teams'),
            icon: '',
            font_family: '',
            css_class: '',
            permissions: [
              { has_capabilities: ['TeamManagement'], has_rule_set_role: ['he_cc_fs_primary_managers'] },
              { has_capabilities: ['TeamManagement'], has_rule_set: ['next_managers'] },
            ],
            target_url: `${dashifiDomain}/{{slug}}/cportal/teams`,
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
          {
            id: '',
            slug: '',
            title: i18n.t('User Uploads'),
            icon: '',
            font_family: '',
            css_class: '',
            permissions: [
              { has_rule_set_role: ['he_cc_fs_primary_managers'] },
              { has_rule_set: [{ next_managers: 'at_work_manager' }] },
            ],
            target_url: `${dashifiDomain}/{{slug}}/cportal/user_uploads`,
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
          {
            id: '',
            slug: '',
            title: i18n.t('Quick Lists'),
            icon: '',
            font_family: '',
            css_class: '',
            permissions: [{ has_rule_set_role: ['he_cc_fs_primary_managers'] }],
            target_url: `${dashifiDomain}/{{slug}}/cportal/rosters`,
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
          {
            id: '',
            slug: '',
            title: i18n.t('Custom Categories'),
            icon: '',
            font_family: '',
            css_class: '',
            permissions: [
              { has_rule_set_role: ['he_cc_fs_primary_managers'] },
              { has_rule_set: [{ next_managers: 'at_work_manager' }] },
            ],
            target_url: `${dashifiDomain}/{{slug}}/cportal/categories`,
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
          {
            id: '',
            slug: '',
            title: i18n.t('Locations'),
            icon: '',
            font_family: '',
            css_class: '',
            permissions: [
              { has_rule_set_role: ['he_cc_fs_primary_managers'] },
              {
                has_rule_set: [
                  { next_managers: 'at_work_manager' },
                  { he_cc_fs_scorm_manager_names: 'scorm_manager' },
                ],
              },
            ],
            target_url: `${dashifiDomain}/{{slug}}/cportal/locations`,
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
        ],
      },
      {
        id: '',
        slug: '',
        title: i18n.t('Insights'),
        icon: 'chart',
        font_family: '',
        css_class: '',
        permissions: [
          { has_rule_set: ['he_cc_fs_scorm_manager_names'] },
          { has_rule_set: ['next_managers'], not_rule_set: [{ next_managers: 'pii_restricted_next_advisor' }] },
          { has_rule_set: ['event_manager_not_cra_content_only'] },
        ],
        target_url: '',
        target_method: '',
        new_window: false,
        subsection: false,
        weight: 100,
        submenu_items: [
          {
            id: '',
            slug: '',
            title: i18n.t('Reports'),
            icon: '',
            font_family: '',
            css_class: '',
            target_url: `${dashifiDomain}/{{slug}}/cportal/reports?force=true`,
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
          {
            id: '',
            slug: '',
            title: i18n.t('Data Exports'),
            icon: '',
            font_family: '',
            css_class: '',
            permissions: [
              { has_capabilities: ['DatafiExport'], has_rule_set_role: ['he_cc_fs_primary_managers'] },
            ],
            target_url: `${adminifiDomain}/{{slug}}/cportal/data_downloads`,
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
          {
            id: '',
            slug: '',
            title: i18n.t('Impact Reports'),
            icon: '',
            font_family: '',
            css_class: '',
            permissions: [
              { has_capabilities: ['FileDownloads'], has_rule_set_role: ['he_cc_fs_primary_managers'] },
              { has_rule_set: [{ he_cc_fs_scorm_manager_names: 'scorm_manager' }] },
            ],
            target_url: `${dashifiDomain}/{{slug}}/cportal/downloads`,
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
        ],
      },
      {
        id: '',
        slug: '',
        title: i18n.t('Assignments'),
        icon: 'clipboard-check',
        font_family: '',
        css_class: '',
        permissions: [{ has_rule_set: ['he_cc_fs_manager_names'] }],
        target_url: `${dashifiDomain}/{{slug}}/cportal/assignments`,
        target_method: '',
        new_window: false,
        subsection: false,
        submenu_items: [],
        weight: 100,
      },
      {
        id: '',
        slug: '',
        title: i18n.t('Programs'),
        icon: 'globe',
        font_family: '',
        css_class: '',
        permissions: [{ has_rule_set: ['advisors'], not_rule_set: ['next_main_managers'] }],
        target_url: '{{next_base}}/cportal/programs',
        target_method: '',
        new_window: false,
        subsection: false,
        submenu_items: [],
        weight: 100,
      },
      {
        id: '',
        slug: '',
        title: i18n.t('Content'),
        icon: 'films',
        font_family: '',
        css_class: '',
        permissions: [{ has_rule_set: ['next_main_managers'] }],
        target_url: '',
        target_method: '',
        new_window: false,
        subsection: false,
        weight: 100,
        submenu_items: [
          {
            id: '',
            slug: '',
            title: i18n.t('Playlists'),
            icon: '',
            font_family: '',
            css_class: '',
            target_url: '{{next_base}}/cportal/admin_playlists',
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
          {
            id: '',
            slug: '',
            title: i18n.t('Programs'),
            icon: '',
            font_family: '',
            css_class: '',
            target_url: '{{next_base}}/cportal/programs',
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
        ],
      },
      {
        id: '',
        slug: '',
        title: i18n.t('Event Content'),
        icon: 'calendar',
        font_family: '',
        css_class: '',
        permissions: [
          { has_capabilities: ['CRAEducation'], has_rule_set: [{ engage_managers: 'event_manager' }] },
        ],
        target_url: '',
        target_method: '',
        new_window: false,
        subsection: false,
        weight: 100,
        submenu_items: [
          {
            id: '',
            slug: '',
            title: i18n.t('Volunteers'),
            icon: '',
            font_family: '',
            css_class: '',
            target_url: '{{engage_base}}/cportal/volunteers',
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
          {
            id: '',
            slug: '',
            title: i18n.t('Work Locations'),
            icon: '',
            font_family: '',
            css_class: '',
            target_url: '{{engage_base}}/cportal/work_locations',
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
          {
            id: '',
            slug: '',
            title: i18n.t('Workshop Topics'),
            icon: '',
            font_family: '',
            css_class: '',
            target_url: '{{engage_base}}/cportal/topics',
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
        ],
      },
      {
        id: '',
        slug: '',
        title: i18n.t('Manage Events'),
        icon: 'calendar',
        font_family: '',
        css_class: '',
        permissions: [
          { has_capabilities: ['EventManagement'], has_rule_set: ['engage_managers'] },
          { has_capabilities: ['CRATracking'], has_rule_set: ['engage_managers'] },
        ],
        target_url: '',
        target_method: '',
        new_window: false,
        subsection: false,
        weight: 100,
        submenu_items: [
          {
            id: '',
            slug: '',
            title: i18n.t('Events'),
            icon: '',
            font_family: '',
            css_class: '',
            target_url: '{{engage_base}}/cportal/events',
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
          {
            id: '',
            slug: '',
            title: i18n.t('Volunteers'),
            icon: '',
            font_family: '',
            css_class: '',
            permissions: [
              {
                not_capabilities: ['EventManagement'],
                has_rule_set: [{ engage_managers: 'event_manager' }],
              },
            ],
            target_url: '{{engage_base}}/cportal/volunteers',
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
          {
            id: '',
            slug: '',
            title: i18n.t('Attendees'),
            icon: '',
            font_family: '',
            css_class: '',
            permissions: [{ has_rule_set: [{ engage_managers: 'event_manager' }] }],
            target_url: '{{engage_base}}/cportal/attendees',
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
          {
            id: '',
            slug: '',
            title: i18n.t('Workshop Topics'),
            icon: '',
            font_family: '',
            css_class: '',
            target_url: '{{engage_base}}/cportal/topics',
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
          {
            id: '',
            slug: '',
            title: i18n.t('Venues'),
            icon: '',
            font_family: '',
            css_class: '',
            target_url: '{{engage_base}}/cportal/venues',
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
          {
            id: '',
            slug: '',
            title: i18n.t('Work Locations'),
            icon: '',
            font_family: '',
            css_class: '',
            permissions: [
              {
                not_capabilities: ['EventManagement'],
                has_rule_set: [{ engage_managers: 'event_manager' }],
              },
            ],
            target_url: '{{engage_base}}/cportal/work_locations',
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
          {
            id: '',
            slug: '',
            title: i18n.t('Partners'),
            icon: '',
            font_family: '',
            css_class: '',
            target_url: '{{engage_base}}/cportal/partners',
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
          {
            id: '',
            slug: '',
            title: i18n.t('Settings'),
            icon: '',
            font_family: '',
            css_class: '',
            permissions: [{ has_rule_set: [{ engage_managers: 'event_manager' }] }],
            target_url: '{{engage_base}}/cportal/settings',
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
        ],
      },
      {
        id: '',
        slug: '',
        title: i18n.t('Communications'),
        icon: 'bell-on',
        font_family: '',
        css_class: '',
        permissions: [{ has_rule_set_role: ['he_cc_fs_primary_managers'] }],
        target_url: `${dashifiDomain}/{{slug}}/cportal/templates`,
        target_method: '',
        new_window: false,
        subsection: false,
        submenu_items: [],
        weight: 100,
      },
      {
        id: '',
        slug: '',
        title: i18n.t('Resource Center'),
        icon: 'book-reader',
        font_family: '',
        css_class: '',
        permissions: [{ has_rule_set: [{ next_managers: 'at_work_manager' }] }],
        target_url: 'https://help.everfi.com/achieve-content',
        target_method: '',
        new_window: true,
        subsection: false,
        submenu_items: [],
        weight: 100,
      },
      {
        id: '',
        slug: '',
        title: i18n.t('Settings'),
        icon: 'settings',
        font_family: '',
        css_class: '',
        permissions: [
          {
            has_capabilities: settingsCapabilities,
            has_rule_set: [{ next_managers: 'at_work_manager' }, { next_managers: 'pii_restricted_next_primary' }],
          },
          {
            has_capabilities: settingsCapabilities,
            not_rule_set: ['advisors'],
          },
          {
            has_capabilities: settingsCapabilities,
            has_rule_set: ['event_manager', 'event_manager_not_cra_content_only'],
          },
          {
            has_capabilities: settingsCapabilities,
            has_rule_set_role: ['he_cc_fs_primary_managers'],
          },
          {
            not_capabilities: ['LegacyAssignments'],
            has_rule_set: ['he_cc_fs_manager_names'],
          },
        ],
        target_url: '',
        target_method: '',
        new_window: false,
        subsection: false,
        weight: 9999,
        submenu_items: [
          {
            id: '',
            slug: '',
            title: i18n.t('Training Periods'),
            icon: '',
            font_family: '',
            css_class: '',
            permissions: [
              { not_capabilities: ['LegacyAssignments'], has_rule_set: ['he_cc_fs_manager_names'] },
            ],
            target_url: `${dashifiDomain}/{{slug}}/cportal/training_periods`,
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
          {
            id: '',
            slug: '',
            title: i18n.t('Single Sign-on'),
            icon: '',
            font_family: '',
            css_class: '',
            permissions: [
              { has_capabilities: ['SamlSso'], has_rule_set: [{ next_managers: 'at_work_manager' }] },
              {
                has_capabilities: ['SamlSso'],
                has_rule_set: [{ next_managers: 'pii_restricted_next_primary' }],
              },
              {
                has_capabilities: ['SamlSso'],
                has_rule_set: [{ engage_managers: 'event_manager' }],
              },
              { has_capabilities: ['SamlSso'], has_rule_set_role: ['he_cc_fs_primary_managers'] },
            ],
            target_url: `${dashifiDomain}/{{slug}}/cportal/identity_providers`,
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
          {
            id: '',
            slug: '',
            title: i18n.t('Embed Content'),
            icon: '',
            font_family: '',
            css_class: '',
            permissions: [
              { has_capabilities: ['EmbedContent'], has_rule_set: [{ next_managers: 'at_work_manager' }] },
            ],
            target_url: `${dashifiDomain}/{{slug}}/cportal/embed_content`,
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
          {
            id: '',
            slug: '',
            title: i18n.t('Data Integration'),
            icon: '',
            font_family: '',
            css_class: '',
            permissions: [
              {
                has_capabilities: ['PublicApiAccess'],
                has_rule_set: ['he_cc_fs_manager_names'],
              },
              {
                has_capabilities: ['PublicApiAccess'],
                has_rule_set: [{ engage_managers: 'event_manager' }],
              },
              {
                has_capabilities: ['PublicApiAccess'],
                has_rule_set: [{ next_managers: 'at_work_manager' }],
              },
            ],
            target_url: `${dashifiDomain}/{{slug}}/cportal/data_integration`,
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
          {
            id: '',
            slug: '',
            title: i18n.t('Webhooks'),
            icon: '',
            font_family: '',
            css_class: '',
            permissions: [
              {
                has_capabilities: ['WebhookAccess'],
                has_rule_set: ['he_cc_fs_manager_names'],
              },
              {
                has_capabilities: ['WebhookAccess'],
                has_rule_set: [{ next_managers: 'at_work_manager' }],
              },
            ],
            target_url: `${dashifiDomain}/{{slug}}/cportal/webhooks`,
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
          {
            id: '',
            slug: '',
            title: i18n.t('Tag Manager'),
            icon: '',
            font_family: '',
            css_class: '',
            permissions: [
              {
                has_capabilities: ['TagManager'],
                has_rule_set: [{ next_managers: 'at_work_manager' }],
              },
            ],
            target_url: `${dashifiDomain}/{{slug}}/cportal/tag_manager`,
            target_method: '',
            new_window: false,
            subsection: false,
            submenu_items: [],
            weight: 100,
          },
        ],
      },
      {
        id: '',
        slug: '',
        title: i18n.t('Help'),
        icon: 'circle-question',
        font_family: '',
        css_class: '',
        permissions: [
          { has_rule_set_role: ['he_cc_fs_primary_managers'] },
          { has_rule_set: [{ he_cc_fs_scorm_manager_names: 'scorm_manager' }] },
        ],
        target_url: 'https://workplacehelp.everfi.com',
        target_method: '',
        new_window: true,
        subsection: false,
        submenu_items: [],
        weight: 100,
      },
      {
        id: '',
        slug: '',
        title: i18n.t('Help'),
        icon: 'circle-question',
        font_family: '',
        css_class: '',
        permissions: [{ has_rule_set: ['engage_managers'] }, { has_rule_set: ['next_managers'] }],
        target_url: 'https://help.everfi.com',
        target_method: '',
        new_window: true,
        subsection: false,
        submenu_items: [],
        weight: 100,
      },
    ],
  };
};

